import { useNavigate, useParams } from 'react-router-dom';
import { MESSAGES, ORDER_BY_NUMBER_QUERY_PARAM, ORDER_BY_STRING_QUERY_PARAM } from 'src/modules/common/constants.ts';
import { Suspense, useCallback, useContext, useDeferredValue, useEffect, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import { skipToken } from '@apollo/client';
import { getFilterComposed } from 'src/modules/common/hooks/getFilterComposed.ts';
import { Device } from 'src/common/Device/Device.tsx';
import { useCheckMainFilter } from 'src/modules/common/hooks/useCheckMainFilter.ts';
import { RuntimeContext } from 'src/runtimeContext';
import { MainLayout } from 'src/modules/common/layouts/MainLayout';
import { initLocationState } from 'src/actuators/locationStateContext.ts';
import { SkeletonPageLayout } from 'src/modules/common/skeletons/SkeletonPageLayout/SkeletonPageLayout';
import { Query } from 'src/modules/common/components/Query/Query';
import { NoData } from 'src/modules/common/components/NoData/NoData';
import { DeviceGrid } from 'src/common/DeviceGrid/DeviceGrid';
import {
    ActuatorProduct,
    ActuatorsListPageData,
    ActuatorsListPageVariables,
    getActuatorsListPageConfig,
} from 'src/actuators/list/ActuatorsListPageMapping.ts';
import { ActuatorIcon } from 'src/modules/common/icons/ActuatorIcon.tsx';
import { prepareActuatorAttributes } from 'src/actuators/prepareActuatorAttributes.ts';
import { useReadUserInput } from 'src/actuators/user-input/useReadUserInput.ts';
import { ActuatorsAppFilterPagePrimaryQueryVariables, DirectionEnum } from 'src/generated/operations.ts';
import { useOrderBy } from 'src/modules/common/hooks/useOrderBy.ts';
import { generateOrderItems } from 'src/utils/generateOrderItems.ts';
import { usePageChange } from 'src/modules/common/hooks/usePageChange.ts';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { showFailSafeAttr } from 'src/actuators/showFailSafeAttr.ts';
import clsx from 'clsx';
import { FilterSidebarComponent } from 'src/common/FilterSidebarComponent/FilterSidebarComponent';
import { detectActuatorType } from 'src/actuators/detectActuatorType';
import { CheckboxHandler } from 'src/modules/common/types/CheckboxHandler.ts';
import { FilterEventDetails } from 'src/modules/common/types/FilterEventDetails.ts';
import { useLocationState } from 'src/actuators/user-input/useLocationState.ts';
import { ActuatorTypes } from 'src/actuators/user-input/calculations/constants.ts';
import { buildDesignDataContentRaw } from 'src/actuators/checkout/buildDesignDataContent.ts';
import { useBelimoTranslate } from 'src/common/hooks/useBelimoTranslate.ts';
import { useGetHomeBreadcrumb } from 'src/actuators/home/useGetHomeBreadcrumb.ts';
import { ActuatorBreadcrumbs } from 'src/actuators/common/ActuatorBreadcrumbs/ActuatorBreadcrumbs';
import { PATHS } from 'src/actuators/common/types/Paths';
import { ActuatorsSelectActuatorButton } from 'src/actuators/list/components/ActuatorsSelectActuatorButton';
import { SelectionsButton } from 'src/projects/components/SelectionsButton.tsx';

const limit = 10;

const stringNames: Array<keyof ActuatorProduct> = [
    'nominalVoltage',
    'failSafeFunction',
    'controlType',
    'integratedSwitch',
    'protection',
];
const numberNames: Array<keyof ActuatorProduct> = [
    'torque',
    'actuatingForce',
    'runningTime',
    'stroke',
];

export const ActuatorsListPage = () => {
    const { userSettings, mandatoryFilterConfig } = useContext(ConfigContext);
    const { isMobile } = useContext(RuntimeContext);
    const [locationState, setLocationState] = useLocationState();
    const pageConfig = useMemo(() => getActuatorsListPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }

    const [
        searchParams,
        filter,
    ] = useFilterLogic([
        ...pageConfig.mainFilterConfig,
        ...pageConfig.filterConfig,
    ]);

    const [
        ,
        filterWithoutMainFilterConfig,
        checkboxHandler,,
        filtersReset,
    ] = useFilterLogic([
        ...pageConfig.filterConfig,
    ]);

    const {
        config: userInputFilterConfig,
        filter: userInputValues,
        state,
    } = useReadUserInput(searchParams, userSettings.currentRegion);

    const navigate = useNavigate();

    const selectedActuatorType = detectActuatorType(filter, navigate);

    const isLinear = selectedActuatorType === ActuatorTypes.Linear;
    const translate = useBelimoTranslate();

    const homePageValue = useGetHomeBreadcrumb({ filter });

    const hasAdditionalFilter = useCheckMainFilter({
        searchParams,
        filter,
        navigate,
        filterConfig: pageConfig.filterConfig,
    });

    const { page } = useParams();

    const currentPage = useMemo(() => {
        const parsedPage = parseInt(page || '1', 10);
        return parsedPage < 1 ? 1 : parsedPage;
    }, [page]);

    const [orderByString, setOrderByString] = useOrderBy(ORDER_BY_STRING_QUERY_PARAM);
    const [orderByNumber, setOrderByNumber] = useOrderBy(ORDER_BY_NUMBER_QUERY_PARAM);

    useEffect(() => {
        if (currentPage.toString() !== locationState.actuatorPage) {
            setLocationState({
                ...locationState,
                actuatorPage: currentPage.toString(),
            });
        }
    }, [currentPage, locationState, setLocationState]);

    const variables = useMemo((): { variables: ActuatorsListPageVariables } | typeof skipToken => {
        if (filter === null) {
            return skipToken;
        }
        const [filterMain] = getFilterComposed([
            ...pageConfig.mainFilterConfig,
            ...pageConfig.filterConfig,
            ...userInputFilterConfig,
        ], { ...filter, ...userInputValues }, mandatoryFilterConfig, userSettings.currentLocale);

        const offset = (currentPage - 1) * limit;

        return {
            variables: {
                filter: filterMain,
                locale: userSettings.currentLocale,
                offset,
                limit,
                orderByString,
                orderByNumber,
            },
        };
    }, [
        orderByNumber,
        orderByString,
        userInputFilterConfig,
        userInputValues,
        filter,
        userSettings,
        currentPage,
        mandatoryFilterConfig,
        pageConfig,
    ]);

    const defVariables = useDeferredValue(variables);

    const isStale = defVariables !== variables;

    const filtersVariables = useMemo((): {
        variables: ActuatorsAppFilterPagePrimaryQueryVariables;
    } | typeof skipToken => {
        if (filter === null) {
            return skipToken;
        }

        const [filterMain] = getFilterComposed(
            [
                ...pageConfig.mainFilterConfig,
                ...pageConfig.filterConfig,
                ...userInputFilterConfig,
            ],
            {
                ...filter,
                ...userInputValues,
            },
            mandatoryFilterConfig,
            userSettings.currentLocale,
        );

        return {
            variables: {
                filter: filterMain,
                locale: userSettings.currentLocale,
                region: userSettings.currentRegion,
                shoppingCenter: userSettings.shoppingCenter ?? '',
            },
        };
    }, [
        userInputValues,
        userInputFilterConfig,
        pageConfig.mainFilterConfig,
        pageConfig.filterConfig,
        filter,
        mandatoryFilterConfig,
        userSettings,
    ]);

    const updatedLocation = useCallback((newPage: number) => setLocationState(
        { ...initLocationState, actuatorPage: newPage.toFixed() },
    ), [setLocationState]);

    const onPageChange = usePageChange(
        (newPage: number) => `${PATHS.Actuators}/${newPage}`,
        updatedLocation,
    );

    const actuatorsCheckboxHandler: CheckboxHandler = useCallback((detail: FilterEventDetails) => {
        onPageChange(1, searchParams);
        return checkboxHandler(detail);
    }, [onPageChange, checkboxHandler, searchParams]);

    const resetActuatorFilters = useCallback(() => {
        onPageChange(1, searchParams);
        filtersReset(pageConfig.filterConfig);
    }, [onPageChange, searchParams, filtersReset, pageConfig.filterConfig]);

    const resetBreadcrumb = useCallback(() => {
        setLocationState({
            ...locationState,
            actuatorLabel: '',
            selectedAccessories: [],
            qty: 1,
        });
    }, [locationState, setLocationState]);

    return (
        <div className="vss-actuators">
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.ActuatorsListAppPageTitle}
                    navigation={null}
                    breadcrumbs={(
                        <ActuatorBreadcrumbs
                            currentPage={PATHS.Actuators}
                            homePageValue={homePageValue}
                            isLinear={isLinear}
                            userInputState={state}
                            locationState={locationState}
                            searchParams={searchParams}
                            resetHandler={locationState.actuatorLabel ? resetBreadcrumb : undefined}
                        />
                    )}
                    selectionButton={(
                        <SelectionsButton searchParams={searchParams} locationState={locationState}/>
                    )}
                />

                <Suspense fallback={<SkeletonPageLayout/>}>
                    <Query<ActuatorsListPageData, ActuatorsListPageVariables>
                        query={pageConfig.query}
                        variables={defVariables}
                    >
                        {({ data }) => {
                            if (data === undefined) {
                                return (
                                    <SkeletonPageLayout/>
                                );
                            }

                            if (data.list.results.length === 0) {
                                return (
                                    <div className={clsx({
                                        'vss-actuators-list-page': true,
                                        'vss-actuators-list-page--mobile': isMobile,
                                    })}
                                    >
                                        <FilterSidebarComponent
                                            label={MESSAGES.modalFilterActuatorsLabel}
                                            filterConfigPrimary={pageConfig.filterConfigPrimary}
                                            filterConfigSecondary={pageConfig.filterConfigSecondary}
                                            queryPrimary={pageConfig.queryPrimary}
                                            genericFilterQuery={pageConfig.genericFilterQuery}
                                            variables={filtersVariables}
                                            checkboxHandler={actuatorsCheckboxHandler}
                                            handleReset={resetActuatorFilters}
                                            filter={filterWithoutMainFilterConfig}
                                        />

                                        <NoData/>
                                    </div>
                                );
                            }

                            const [firstProduct] = data.list.results;

                            const orderItemsString = generateOrderItems(
                                stringNames.filter((name) => !!firstProduct[name]) as string[],
                                pageConfig?.orderItemsConfig,
                                DirectionEnum.Asc,
                            );

                            const orderItemsNumber = generateOrderItems(
                                numberNames.filter((name) => !!firstProduct[name]) as string[],
                                pageConfig?.orderItemsConfig,
                                DirectionEnum.Asc,
                            );

                            return (
                                <div className={clsx({
                                    'vss-actuators-list-page': true,
                                    'vss-actuators-list-page--mobile': isMobile,
                                })}
                                >
                                    <FilterSidebarComponent
                                        label={MESSAGES.modalFilterActuatorsLabel}
                                        filterConfigPrimary={pageConfig.filterConfigPrimary}
                                        filterConfigSecondary={pageConfig.filterConfigSecondary}
                                        queryPrimary={pageConfig.queryPrimary}
                                        genericFilterQuery={pageConfig.genericFilterQuery}
                                        variables={filtersVariables}
                                        checkboxHandler={actuatorsCheckboxHandler}
                                        handleReset={resetActuatorFilters}
                                        filter={filterWithoutMainFilterConfig}
                                    />

                                    <div className={clsx({
                                        'vss-actuators-list-page__list': true,
                                        'vss-actuators-list-page__list--stale': isStale,
                                    })}
                                    >

                                        <DeviceGrid
                                            isMobile={isMobile}
                                            hasAdditionalFilter={hasAdditionalFilter}
                                            currentPage={currentPage}
                                            pageSize={limit}
                                            total={data.list.count || 0}
                                            onPageChange={(p: number) => onPageChange(p, searchParams)}
                                            defaultOrderLabel={MESSAGES.Label}
                                            orderItems={[
                                                ...orderItemsNumber,
                                                ...orderItemsString,
                                            ]}
                                            orderSelected={[
                                                ...orderByNumber,
                                                ...orderByString,
                                            ]}
                                            onOrderByChange={(newOrder) => {
                                                if (newOrder.length > 0 &&
                                            orderItemsNumber.find(([, id]) => id === newOrder[0].id) !== undefined) {
                                                    onPageChange(
                                                        1,
                                                        setOrderByString(setOrderByNumber(searchParams, newOrder), []),
                                                    );
                                                } else {
                                                    onPageChange(
                                                        1,
                                                        setOrderByNumber(setOrderByString(searchParams, newOrder), []),
                                                    );
                                                }
                                            }}
                                        >
                                            {data?.list.results.map((product) => (
                                                <Device
                                                    key={`${product.ID}_${product.Label}`}
                                                    defaultImage={<ActuatorIcon/>}
                                                    product={product}
                                                    attributes={prepareActuatorAttributes(
                                                        product,
                                                        showFailSafeAttr(filter),
                                                    )}
                                                    designData={buildDesignDataContentRaw({
                                                        userInput: state,
                                                        currentLocale: userSettings.currentLocale,
                                                        currentRegion: userSettings.currentRegion,
                                                        isLinear,
                                                    }).map((v) => (typeof v !== 'string' ? translate(
                                                        v.key,
                                                        v.detectNamespace,
                                                    ) : v)).join('')}
                                                    selectButton={(
                                                        <ActuatorsSelectActuatorButton
                                                            label={product.Label}
                                                            currentPage={currentPage}
                                                            locationState={locationState}
                                                        />
                                                    )}
                                                />
                                            ))}
                                        </DeviceGrid>
                                    </div>
                                </div>
                            );
                        }}
                    </Query>
                </Suspense>
            </MainLayout>
        </div>
    );
};
